import { trackEvent } from "../analytics/analytics";
import apiClient, {
  BaseOperationResult,
  OperationResult,
} from "../../services/apiClient";
import { logError } from "../../services/logging";
import { EMPTY_GUID } from "../data/data-types";
import { formatDateObj } from "../data/time";
import { CatalogBuilder } from "./catalog.builder";
import { HubCatalog, SiteHubData } from "./catalog.hub";
import { ProductContainerFields } from "./catalog.products";
import { CatalogRef, Catalog, mapCatalog, CatalogItemRef } from "./catalogs";
import { journeyManager } from "../guide/journeyManager";
import { loadAccountInfo } from "../accounts/account.service";
import { syncIntercomCounts } from "../../services/intercom";

// I47 - a create, copy, delete, publish or unpublish moves the counts First Catalog V2 reads in
// Intercom. Fire-and-forget: a failed read must never fail the catalog operation.
const refreshIntercomCounts = () => {
  loadAccountInfo()
    .then((info) => syncIntercomCounts(info?.summary))
    .catch(() => undefined);
};

// Last saved published state per catalog, so autosave (every 20 s in the designer) does not
// re-read the summary. The first save of an already published catalog after a page load reads
// once, because its earlier state is unknown here; syncIntercomCounts drops it as unchanged.
const savedPublished = new Map<string, boolean>();

export const loadCatalogsDb = async (
  storageUrl: string
): Promise<CatalogRef[]> => {
  const response = await apiClient.get<Catalog[]>("/catalogs/list");
  return response.data.map((catalog) => mapCatalog(catalog, storageUrl));
};

export const loadCatalog = async (catalogId: string): Promise<Catalog> => {
  const response = await apiClient.get<Catalog>(
    "/catalogs/get?id=" + catalogId
  );
  return response.data;
};

export type CatalogBackup = {
  id: string;
  isManual: boolean;
  name: string;
  created: string;
};

export type CatalogSaveResult = OperationResult<{
  catalog: Catalog;
}>;

export const saveCatalog = async (
  catalog: Catalog,
  backupType?: string
): Promise<CatalogSaveResult> => {
  const isNew = catalog.id === EMPTY_GUID;
  if (!isNew) {
    trackEvent({
      category: "catalog",
      action: "update",
      label: catalog.id + " : " + catalog.name,
      feature: true,
    });
  }
  // The create event is deliberately NOT fired here. It used to be, with label "new : <name>",
  // which meant catalog/create was the only catalog act carrying no catalog id - so a catalog
  // could never be followed from creation to publish. The id only exists after the save returns,
  // so the event moved below. Side effect, and it is the correct one: a failed save no longer
  // emits a create event.

  // Catalog saves can be slow with large product sets - allow 2 minutes
  // Backend allows 5min (CatalogMapper.cs db.SetTimeout(300))
  const response = await apiClient.post<CatalogSaveResult>(
    "/catalogs/save",
    {
      catalog,
      backupType,
      backupName: formatDateObj(new Date(), true, "long"),
    },
    { timeout: 120000 } // 2 minutes for heavy catalog saves
  );

  const saved = response.data?.value?.catalog;
  if (response.data?.success && saved?.id) {
    const isPublished = (saved.status ?? catalog.status) === "Published";
    const wasPublished = savedPublished.get(saved.id) ?? false;
    savedPublished.set(saved.id, isPublished);
    if (isNew || isPublished !== wasPublished) refreshIntercomCounts();
  }
  if (isNew && saved?.id) {
    trackEvent({
      category: "catalog",
      action: "create",
      label: saved.id + " : " + saved.name,
      feature: true,
      assets: ["catalogs"],
    });
  }

  return response.data;
};

export const deleteCatalog = async (
  catalogId: string
): Promise<OperationResult<string>> => {
  trackEvent({ category: "catalog", action: "delete", label: catalogId });
  const response = await apiClient.post<OperationResult<string>>(
    "/catalogs/delete-catalog?catalogId=" + catalogId
  );
  if (response.data?.success) refreshIntercomCounts();
  return response.data;
};

export const copyCatalog = async (
  catalogId: string
): Promise<OperationResult<Catalog>> => {
  trackEvent({ category: "catalog", action: "copy", label: catalogId });
  const response = await apiClient.post<OperationResult<Catalog>>(
    "/catalogs/copy-catalog?catalogId=" + catalogId
  );
  if (response.data?.success) refreshIntercomCounts();
  return response.data;
};

/**
 * S69 - drop the CDN copy of this catalog's shared PDF.
 *
 * Regenerating already purges the edge, so this is the escape hatch for what that cannot reach: a
 * purge that failed while Cloudflare was down, a PDF produced by V1's old engine, or an image inside
 * the catalog that changed without the catalog being rebuilt.
 */
export const clearCatalogCache = async (
  catalogId: string
): Promise<OperationResult<string>> => {
  trackEvent({ category: "catalog", action: "clear-cache", label: catalogId });
  const response = await apiClient.post<OperationResult<string>>(
    "/catalogs/clear-cache?catalogId=" + catalogId
  );
  return response.data;
};

export const loadCatalogBackups = async (
  catalogId: string
): Promise<CatalogBackup[]> => {
  const response = await apiClient.get<CatalogBackup[]>(
    "/catalogs/backups?catalogId=" + catalogId
  );
  return response.data;
};

/**
 * S29 — one defect found on a catalog about to be published. `source` says which lane found it:
 * "structural" is the deterministic text scan, "visual" is what the Concierge saw in the render.
 */
export type PrePublishFinding = {
  severity: "blocker" | "warn";
  page?: number | null;
  issue: string;
  fix?: string | null;
  /** "render" = measured in a Chrome layout of the print view (S58 cut-off text). */
  source: "structural" | "visual" | "render";
};

export type PrePublishCheck = {
  /** False when RecoveryMode is off (the shipped default) — the UI renders nothing. */
  enabled: boolean;
  /** "halt" = publish withheld until fixed or overridden. "nudge" = named, publish stays open. */
  mode: "off" | "halt" | "nudge";
  clean: boolean;
  blockerCount: number;
  warningCount: number;
  visualCheckedAt?: string | null;
  findings: PrePublishFinding[];
};

export const loadPrePublishCheck = async (
  catalogId: string
): Promise<PrePublishCheck | null> => {
  try {
    const response = await apiClient.get<PrePublishCheck>(
      "/catalogs/prepublish-check?catalogId=" + catalogId
    );
    return response.data;
  } catch (e) {
    // A check that cannot run must never block the user's own publish. Fail open, stay quiet.
    logError(e, "loadPrePublishCheck " + catalogId);
    return null;
  }
};

/** S58 Ph B — cut-off text, from a Chrome layout. Seconds, not ms, so it is its own call. Fails open. */
export const loadPrePublishCutTextCheck = async (
  catalogId: string
): Promise<PrePublishCheck | null> => {
  try {
    const response = await apiClient.get<PrePublishCheck>(
      "/catalogs/prepublish-check/cut-text?catalogId=" + catalogId
    );
    return response.data;
  } catch (e) {
    logError(e, "loadPrePublishCutTextCheck " + catalogId);
    return null;
  }
};

export const restoreCatalogBackup = async (
  catalogId: string,
  backupId: string
): Promise<OperationResult<Catalog>> => {
  trackEvent({ category: "catalog", action: "restore", label: catalogId });
  const response = await apiClient.post<OperationResult<Catalog>>(
    "/catalogs/restore-backup?catalogId=" + catalogId + "&backupId=" + backupId
  );
  return response.data;
};

const createCatalogPages = async (
  builder: CatalogBuilder,
  pageItem: CatalogItemRef
) => {
  try {
    const subcatalog = pageItem.info.subcatalog;
    const cr = builder.data.catalogs.find(
      (c) => c.catalog.id === subcatalog.catalog.id
    );
    if (!cr) return;

    cr.catalog = await loadCatalog(subcatalog.catalog.id);

    const b = new CatalogBuilder(subcatalog.catalog);
    b.setData(builder.data);

    const masterConfig = pageItem.info.config;

    subcatalog.pages = b.pages
      .filter((p) => p.pageItem?.di.PageType !== "toc")
      .map((p) => {
        const page = {
          ...p,
          readonly: true,
          pageId: pageItem.di.PageId,
          info: {
            ...p.pageItem.info,
            config: { ...masterConfig, ...p.pageItem.info.config },
          },
        };
        //updatePage(builder, page);
        return page;
      });
  } catch (e) {
    logError(e, "subcatalog load");
  }
};
export const loadSiteHub = async (): Promise<SiteHubData> => {
  const response = await apiClient.get<SiteHubData>("/catalogs/kits");
  return response.data;
};

export const importCatalogKit = async (
  catalog: HubCatalog,
  fields: ProductContainerFields,
  withProducts: boolean
): Promise<OperationResult<Catalog>> => {
  trackEvent({
    category: "catalog",
    action: "import",
    label: catalog.catalogId + " : " + catalog.hubEntity.link,
    feature: true,
  });
  const response = await apiClient.post<OperationResult<Catalog>>(
    "/catalogs/import-kit",
    {
      catalogId: catalog.catalogId,
      withProducts,
      ...fields,
    }
  );
  journeyManager.updateJourneyProgress("catalog_creation", "catalog_template", {
    catalog: catalog.title,
  });
  if (response.data?.success) refreshIntercomCounts();
  return response.data;
};

export const prepareCatalogKit = async (
  catalog: HubCatalog
): Promise<BaseOperationResult> => {
  trackEvent({
    category: "catalog",
    action: "prepare-kit",
    label: catalog.catalogId + " : " + catalog.hubEntity.link,
  });
  const response = await apiClient.post<OperationResult<Catalog>>(
    "/catalogs/prepare-kit?catalogId=" + catalog.catalogId
  );
  return response.data;
};

export const loadStructureData = async (
  builder: CatalogBuilder,
  initial: boolean
) => {
  const forLoadSubcatalogs = builder.pages
    .map((p) => p.pageItem)
    .filter(
      (s) => s?.info.subcatalog && (initial || !s?.info.subcatalog.pages)
    );

  if (!forLoadSubcatalogs.length) return false;

  const promises = forLoadSubcatalogs.map(async (s) => {
    await createCatalogPages(builder, s);
  });

  await Promise.all(promises);

  builder.refreshStructure();
  return true;
};
